/**
 * Extracts text content from a Contentful rich text node recursively
 */
function extractTextContent(node: any): string {
  if (!node) return "";
  if (node.nodeType === "text") return node.value || "";
  if (Array.isArray(node.content)) {
    return node.content.map(extractTextContent).join("");
  }
  return "";
}

/**
 * Checks if a table cell node is empty (no text content)
 */
function isCellEmpty(cell: any): boolean {
  if (!cell || cell.nodeType !== "table-cell") return true;
  const text = extractTextContent(cell);
  return !text || text.trim() === "";
}

/**
 * Merges empty 3rd and 4th cells into previous cells within each row
 */
function mergeRowCells(node: any): any {
  if (!node || typeof node !== "object") return node;

  // Handle table-row nodes directly
  if (node.nodeType === "table-row") {
    const cells = node.content?.filter((n: any) => n.nodeType === "table-cell");
    if (!cells || cells.length < 3) return node;

    // Process right-to-left: first check 4th, then 3rd
    // This ensures proper cascading merges when both are empty

    // Check if 4th cell (index 3) exists and is empty
    if (cells.length >= 4) {
      const cell3 = cells[2]; // 3rd cell
      const cell4 = cells[3]; // 4th cell

      if (isCellEmpty(cell4)) {
        console.log(
          `[richTextTransform] Merging cell 4 into cell 3 (cell 4 is empty)`,
        );

        // Remove cell 4 from the row's content
        node.content = node.content.filter((n: any) => n !== cell4);

        // Mark cell 3 as merged with colspan 2
        const cell3Index = node.content.findIndex((n: any) => n === cell3);
        if (cell3Index !== -1) {
          node.content[cell3Index] = {
            ...cell3,
            data: {
              ...cell3.data,
              className: `${cell3.data?.className || ""} merged-cell`.trim(),
              colspan: 2,
            },
          };
        }
      }
    }

    // Refresh cells array after potential modification
    const updatedCells = node.content?.filter(
      (n: any) => n.nodeType === "table-cell",
    );

    // Check if 3rd cell (index 2) is empty
    if (updatedCells && updatedCells.length >= 3) {
      const cell2 = updatedCells[1]; // 2nd cell
      const cell3 = updatedCells[2]; // 3rd cell

      if (isCellEmpty(cell3)) {
        console.log(
          `[richTextTransform] Merging cell 3 into cell 2 (cell 3 is empty)`,
        );

        // Remove cell 3 from the row's content
        node.content = node.content.filter((n: any) => n !== cell3);

        // Calculate colspan: if cell3 already had a colspan, add it, otherwise span 2
        const existingColspan = cell3.data?.colspan || 1;
        const newColspan = 1 + existingColspan;

        // Mark cell 2 as merged with appropriate colspan
        const cell2Index = node.content.findIndex((n: any) => n === cell2);
        if (cell2Index !== -1) {
          node.content[cell2Index] = {
            ...cell2,
            data: {
              ...cell2.data,
              className: `${cell2.data?.className || ""} merged-cell`.trim(),
              colspan: newColspan,
            },
          };
        }
      }
    }

    return node;
  }

  // Recurse into content array
  if (Array.isArray(node.content)) {
    node.content = node.content.map((child: any) => mergeRowCells(child));
  }

  return node;
}

/**
 * Transforms Contentful rich text JSON to merge empty table rows
 * Deep clones before modifying to avoid mutating original
 */
export function transformRichText(json: any): any {
  if (!json) return json;
  console.log("[richTextTransform] Transforming rich text...");
  const cloned = JSON.parse(JSON.stringify(json));
  const result = mergeRowCells(cloned);
  console.log("[richTextTransform] Transform complete");
  return result;
}
